import { useMemo, useState } from 'react';
import { useApp } from '../../data/store.tsx';
import type { AppActions } from '../../data/store.tsx';
import { browseLineup } from '../../engine/lineup-browse.ts';
import { fmtTime } from '../../engine/time.ts';
import type { SeenBefore } from '../../engine/preferences.ts';
import type { Artist, Note, Rating } from '../../engine/types.ts';
import '../../styles/lineup.css';

const ANCHORS = [
  { at: 1, text: 'would rather be somewhere else' },
  { at: 5, text: 'happy to be there if nothing better is on' },
  { at: 10, text: 'this is why I bought the ticket' },
];
const SEEN_OPTIONS: { value: SeenBefore; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Not sure' },
];
const TAGS = ['meaningful', 'nostalgic', 'uplifting', 'cathartic', 'comforting', 'curious', 'overwhelming'];
const NO_SETS: string[] = [];

function ratingLabel(rating: Rating | undefined) {
  if (!rating) return 'Unrated';
  const source = rating.source === 'manual' ? 'Your rating'
    : rating.source === 'imported' ? 'Imported rating' : 'Preference score';
  return `${source} ${rating.score}/10`;
}

function LineupRow({ artist, rating, note, setTimes, actions }: {
  artist: Artist;
  rating: Rating | undefined;
  note: Note | undefined;
  setTimes: string[];
  actions: AppActions;
}) {
  const [open, setOpen] = useState(false);
  const score = rating?.score ?? 5;
  return (
    <details className="lineup-row" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        <span className="lineup-row-content">
          <span className="lineup-row-title">
            <strong>{artist.name}</strong>
            <span className={`lineup-rating${rating?.source === 'manual' ? ' lineup-rating--manual' : ''}`}>
              {ratingLabel(rating)}
            </span>
          </span>
          <span className="lineup-set-times">
            {setTimes.length ? setTimes.map((time, index) => <span key={index}>{time}</span>)
              : <span>Set time unavailable</span>}
          </span>
        </span>
        <svg className="lineup-chevron" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </summary>
      {open && (
        <div className="lineup-controls">
          {artist.genreTags.length > 0 && <p className="muted lineup-genres">{artist.genreTags.join(', ')}</p>}
          <label className="lineup-score-label" htmlFor={`rating-${artist.id}`}>Rate {artist.name}</label>
          <div className="slider">
            <span className="tabular muted">1</span>
            <input id={`rating-${artist.id}`} type="range" min={1} max={10} step={1}
              value={score} aria-valuetext={`${score} out of 10`}
              onChange={(event) => void actions.rate(artist.id, Number(event.target.value))} />
            <span className="tabular">{score}/10</span>
          </div>
          {rating?.source !== 'manual' && (
            <button type="button" className="small" onClick={() => void actions.rate(artist.id, score)}>
              {rating ? `Use ${score}/10 as my rating` : `Rate ${score}/10`}
            </button>
          )}
          <div className="lineup-anchors">
            {ANCHORS.map((anchor) => <span key={anchor.at}>{anchor.at}: {anchor.text}</span>)}
          </div>
          <fieldset className="inline-choice">
            <legend>Seen {artist.name} live before?</legend>
            <div className="seenrow">
              {SEEN_OPTIONS.map((option) => (
                <label key={option.value}>
                  <input type="radio" name={`seen-${artist.id}`} value={option.value}
                    checked={(note?.seenBefore ?? 'unknown') === option.value}
                    onChange={() => void actions.setSeenBefore(artist.id, option.value)} />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="tagrow" role="group" aria-label={`Tags for ${artist.name}`}>
            {TAGS.map((name) => {
              const selected = (note?.tags ?? []).some((tag) => tag.name === name && tag.confirmed);
              return (
                <button key={name} type="button" className={selected ? 'small active' : 'small ghost'}
                  aria-pressed={selected} onClick={() => void actions.toggleTag(artist.id, name)}>{name}</button>
              );
            })}
          </div>
          <label className="lineup-note" htmlFor={`note-${artist.id}`}>
            <span>Notes for {artist.name}</span>
            <textarea id={`note-${artist.id}`} rows={2} placeholder="Add a note (optional)"
              defaultValue={note?.body ?? ''}
              onBlur={(event) => void actions.writeNote(artist.id, event.target.value)} />
          </label>
        </div>
      )}
    </details>
  );
}

export function RateScreen() {
  const { state, actions, derived } = useApp();
  const [query, setQuery] = useState('');
  const [onlyMyRatings, setOnlyMyRatings] = useState(false);
  const rows = useMemo(() => browseLineup(state.artists, state.ratings, state.userId, query, onlyMyRatings),
    [state.artists, state.ratings, state.userId, query, onlyMyRatings]);
  const notesByArtist = useMemo(() => new Map(state.notes.filter((note) => note.userId === state.userId)
    .map((note) => [note.artistId, note])), [state.notes, state.userId]);
  const timesByArtist = useMemo(() => {
    const days = new Map(state.days.map((day) => [day.id, day.label]));
    const times = new Map<string, string[]>();
    // Published starts are known; catalog end times are estimates, so omit them here.
    for (const set of [...state.sets].sort((a, b) => a.startTs - b.startTs)) {
      const label = `${days.get(set.dayId) ?? 'Day unavailable'} · ${fmtTime(set.startTs)} · ${derived.stageById.get(set.stageId)?.name ?? 'Stage unavailable'}`;
      const artistTimes = times.get(set.artistId) ?? [];
      artistTimes.push(label);
      times.set(set.artistId, artistTimes);
    }
    return times;
  }, [state.sets, state.days, derived.stageById]);

  return (
    <div className="lineup-page">
      <h1>Lineup</h1>
      <p className="lede">Browse artists and set times. Rate artists to help build your schedule.</p>
      <div className="panel lineup-toolbar">
        <label className="lineup-search" htmlFor="lineup-search">
          <span>Search artists</span>
          <input id="lineup-search" type="search" placeholder="Artist name" value={query}
            onChange={(event) => setQuery(event.target.value)} />
        </label>
        <div className="viewtoggle lineup-filters" role="group" aria-label="Show artists">
          <button type="button" aria-pressed={!onlyMyRatings} onClick={() => setOnlyMyRatings(false)}>All artists</button>
          <button type="button" aria-pressed={onlyMyRatings} onClick={() => setOnlyMyRatings(true)}>My ratings</button>
        </div>
        <p className="lineup-count muted" role="status">{rows.length} {rows.length === 1 ? 'artist' : 'artists'}</p>
      </div>
      <p className="lineup-time-note muted">Published start times · venue time (EDT)</p>
      {rows.length ? (
        <div className="panel lineup-list" aria-label="Artists">
          {rows.map(({ artist, rating }) => (
            <LineupRow key={artist.id} artist={artist} rating={rating} note={notesByArtist.get(artist.id)}
              setTimes={timesByArtist.get(artist.id) ?? NO_SETS} actions={actions} />
          ))}
        </div>
      ) : (
        <p className="panel">{query.trim() ? 'No artists match your search.'
          : 'No ratings yet. Choose All artists to find someone to rate.'}</p>
      )}
    </div>
  );
}
